<template>
    <card-normal
        :class="{
            'chat-card': true,
            'chat-card--active': props.active,
        }"
        without-padding
        :border-color="props.active ? 'primary' : 'gray-200'"
        background-color="white"
        style="border-width: 2px"
    >
        <flex-container
            direction="column"
            :padding="Size.sm"
            :gap="Size.md"
        >
            <flex-container
                justify-content="space-between"
                align-items="center"
                no-wrap
            >
                <flex-container
                    size="auto"
                    align-items="center"
                    :gap="Size.sm"
                >
                    <button-plain-icon
                        :icon="props.favorited || props.favoriteDisabled ? SolidStarIcon : StarIcon"
                        :color="props.favorited ? 'yellow-600' : 'gray-400'"
                        is-custom-icon
                        :size="IconSize.sm"
                        :disabled="props.favoriteDisabled"
                        @click="handleFavoriteClicked($event)"
                    />
                    <chip-label
                        v-if="mailCount !== undefined && mailCount > 0"
                        color="turquoise-500"
                    >
                        <svg-icon
                            :icon="EnvelopeIcon"
                            is-custom-icon
                            :size="IconSize.sm"
                        />
                        <span>{{ mailCount }}</span>
                    </chip-label>
                </flex-container>

                <text-decorator :size="FontSize.xs">
                    {{ formatDayTime(chat.createdAt) }}
                </text-decorator>
            </flex-container>

            <span class="chat-card__content">
                {{ chat.interactions[0].content }}
            </span>

            <flex-container v-if="chat.tags.length > 0">
                <chip-container>
                    <chip-label
                        v-for="tag in chat.tags.slice(0, 2)"
                        :key="tag.id"
                        background-color="gray-100"
                        border-color="gray-200"
                    >
                        <span
                            class="chat-card__dot"
                            :style="{
                                backgroundColor: tag.color,
                            }"
                        />
                        <text-decorator :size="FontSize.xs">
                            {{ tag.tag }}
                        </text-decorator>
                    </chip-label>
                    <chip-label
                        v-if="chat.tags.length > 2"
                        background-color="gray-200"
                        border-color="gray-200"
                    >
                        <text-decorator :size="FontSize.xs">
                            +{{ chat.tags.length - 2 }}
                        </text-decorator>
                    </chip-label>
                </chip-container>
            </flex-container>
        </flex-container>
    </card-normal>
</template>

<script setup lang="ts">
    import StarIcon from '@keyvue/one-awesome-icons/icons/regular/star.svg?component';
    import SolidStarIcon from '@keyvue/one-awesome-icons/icons/solid/star.svg?component';
    import EnvelopeIcon from '@keyvue/one-awesome-icons/icons/regular/envelope.svg?component';

    import type { Assistent, Chat } from '@keyvue/one-api-client';
    import { FontSize, IconSize, Size } from '@keyvue/one-ui';
    import { formatDayTime } from '@/shared/utils/date';

    const props = defineProps<{
        assistent: Assistent,
        chat: Chat,
        mailCount?: number,
        active?: boolean,
        favorited?: boolean,
        favoriteDisabled?: boolean
    }>();

    const emit = defineEmits<{
        (e: 'favorite-clicked', params: { chat: Chat }) : void
    }>();

    function handleFavoriteClicked(event: Event) {
        emit('favorite-clicked', { chat: props.chat });
        event.stopPropagation();
    }
</script>

<style scoped lang="scss">
    .chat-card {
        cursor: pointer;

        &--active {
            @include box-shadow-360();
        }

        &__dot {
            width: 5px;
            height: 5px;
            border-radius: 50%;
        }

        &__content {
            display: block;
            width: 100%;
            font-size: font-size(sm);
            line-height: var(--text-description--line-height, 1.2em);
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
        }
    }
</style>
